/**
 * GL server mount gate (ROW1 blocker B2 cure): before any GL surface mounts,
 * the client asks the server's single audience authority — GET /gl/status ->
 * {allowed} — whether THIS caller is actually served. The build flags remain
 * the pre-gate (a flag-off build stays byte-identical dark and never calls
 * this); the server answer decides the mount, so a cohort flip renders GL
 * surfaces ONLY for real cohort members instead of mounting broken affordances
 * for the whole authenticated base.
 *
 * FAIL-CLOSED: a 404 (server flag off — the bean is absent), any non-2xx, a
 * malformed body, or a network failure all read as "hidden". A confirmed
 * answer is cached for the session (the audience does not flap); an errored
 * probe is NOT cached, so a transient blip retries on the next mount.
 */
import { useEffect, useState } from "react";
import { glFetch } from "./gl-fetch";
import { glAvailable } from "./gl-audience";

let cached = null; // null | Promise<boolean> (settled true/false answers only)

export const glServerAllows = () => {
  if (cached === null) {
    const probe = glFetch("/gl/status").then((res) => {
      const allowed = !!(res && res.ok && res.data && res.data.allowed === true);
      if (!res || (!res.ok && res.status === 0)) {
        cached = null; // network blip — do not latch hidden for the session
      }
      return allowed;
    });
    cached = probe;
  }
  return cached;
};

/** Test/session hook: forget the cached server answer. */
export const glStatusReset = () => {
  cached = null;
};

/**
 * Full status: {allowed, settled}. `allowed` composes the sync build-flag
 * pre-gate with the server's confirmed {allowed:true}; `settled` distinguishes
 * "probe still in flight" from a real denial (route-level surfaces redirect
 * only on a SETTLED denial; inline surfaces can just stay hidden either way).
 * With the build flags off, the probe never fires and the result settles
 * denied immediately — a flag-off build stays byte-identical dark.
 */
export const useGlStatus = () => {
  const buildAllowed = glAvailable();
  const [state, setState] = useState({ allowed: false, settled: !buildAllowed });
  useEffect(() => {
    if (!buildAllowed) {
      return undefined;
    }
    let alive = true;
    glServerAllows().then((v) => {
      if (alive) {
        setState({ allowed: v, settled: true });
      }
    });
    return () => {
      alive = false;
    };
  }, [buildAllowed]);
  return { allowed: buildAllowed && state.allowed, settled: state.settled };
};

/**
 * The single mount condition for inline GL surfaces: build flags (sync
 * pre-gate) AND the server's confirmed {allowed:true}. Renders start hidden
 * and appear only after confirmation — never the reverse.
 */
export const useGlAllowed = () => useGlStatus().allowed;
